<template>
  <b-container>
    <b-row class="justify-content-center">
      <b-col md="10" lg="8" v-if="data">
        <b-breadcrumb v-if="fromAdmin">
          <b-breadcrumb-item to="/admin">Admin</b-breadcrumb-item>
          <b-breadcrumb-item to="/admin/resources">Resources</b-breadcrumb-item>
          <b-breadcrumb-item active
            ><span v-if="isCreation">Create</span
            ><span v-else>Edit</span> Resource</b-breadcrumb-item
          >
        </b-breadcrumb>

        <h1>
          <span v-if="isCreation">Create</span><span v-else>Edit</span> Resource
        </h1>

        <b-card class="mb-3">
          <b-form @submit.prevent="submit" enctype="multipart/form-data">
            <educational-item-info-form
              ref="item_form"
              :data="data"
              :errors="errors"
              :loading="loading"
            />

            <div class="d-flex justify-content-end">
              <b-btn
                type="submit"
                variant="secondary"
                size="lg"
                :disabled="loading"
              >
                <span v-if="!loading"
                  ><span v-if="isCreation">Create</span
                  ><span v-if="!isCreation">Edit</span> Resource</span
                >
                <b-spinner v-if="loading" small></b-spinner>
              </b-btn>
            </div>
          </b-form>
        </b-card>

        <!-- Add list of views and reviews -->
        <b-row v-if="fromAdmin" class="list-card">
          <b-col cols="12" md="6">
            <b-card class="h-100">
              <h3>
                {{ data.view_count }} View<span v-if="data.review_count != 1"
                  >s</span
                >
              </h3>
              <educational-item-view-list
                :uuid="uuid"
              ></educational-item-view-list>
            </b-card>
          </b-col>
          <b-col cols="12" md="6">
            <b-card class="h-100">
              <!-- Header here -->
              <h2 class="mb-0">{{ data.avg_rating }}/5</h2>
              <h3>
                {{ data.review_count }} Rating<span
                  v-if="data.review_count != 1"
                  >s</span
                >
              </h3>
              <educational-item-review-list
                :uuid="uuid"
                :fromAdmin="true"
              ></educational-item-review-list>
            </b-card>
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import EducationalItemInfoForm from '@/components/educational_resources/EducationalItemInfoForm'
import EducationalItemReviewList from '@/components/educational_resources/EducationalItemReviewList'
import EducationalItemViewList from '../../../components/educational_resources/EducationalItemViewList.vue'

export default {
  name: 'EditEducationalItem',
  components: {
    EducationalItemInfoForm,
    EducationalItemReviewList,
    EducationalItemViewList,
  },
  data() {
    return {
      uuid: this.$route.params.uuid,
      isCreation: true,
      fromAdmin: this.$route.name == 'AdminEditResource',
      apiBase: '',
      data: false,
      errors: {},
      loading: false,
    }
  },
  mounted() {
    // React to the route to make sure that we're showing admins/users the proper views
    if (this.$route.params.uuid) {
      this.isCreation = false
    }
    this.apiBase = this.fromAdmin ? '/admin/resources' : '/resources'
    if (!this.isCreation) {
      this.loadResource()
    } else {
      this.data = {}
    }
  },
  methods: {
    loadResource() {
      if (this.isCreation) {
        return
      }

      this.loading = true
      this.errors = {}

      this.$http
        .get(`${this.apiBase}/${this.uuid}`)
        .then(
          (res) => {
            if (res.data.data.skills && res.data.data.skills.length > 0) {
              res.data.data.skills = res.data.data.skills.map((s) => s.skill_id)
            }
            this.data = res.data.data
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    submit() {
      if (this.isCreation) {
        this.createResource()
      } else {
        this.updateResource()
      }
    },
    createResource() {
      if (!this.isCreation) {
        return
      }

      this.loading = true
      const resource = this.$refs.item_form.resource
      const formData = new FormData()
      for (let key in resource) {
        if (key == 'file') {
          continue
        }

        formData.append(key, resource[key])
      }
      // If we don't need to use the file reader, just head straight to the creation step
      if (!this.requiresUpload(resource.item_type)) {
        this.sendCreationRequest(formData)
        return
      }

      if (!resource.file) {
        this.errors = { file: ["Drag and drop not supported"] }
        this.loading = false
        return
      }

      formData.append('fileToSave', resource.file)
      this.sendCreationRequest(formData)
    },
    requiresUpload(itemType) {
      const uploadTypes = ['pdf', 'ppt', 'mp4', 'xls']
      return uploadTypes.indexOf(itemType) >= 0
    },
    sendCreationRequest(formData) {
      if (!this.isCreation) {
        return
      }

      this.$http
        .post(`${this.apiBase}/create`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(
          (res) => {
            this.uuid = res.data.data.uuid
            // Send the user to the proper list view (admins stay within the panel)
            this.$router.push(
              this.fromAdmin ? `/admin/resources` : '/resources'
            )
          },
          (err) => {
            // console.log(err)
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
    updateResource() {
      if (this.isCreation) {
        return
      }

      this.loading = true

      const resource = this.$refs.item_form.resource
      const formData = new FormData()
      formData.append('title', resource.title)
      formData.append('description', resource.description)
      formData.append('skills', resource.skills)
      this.$http
        .post(`${this.apiBase}/${this.data.uuid}/edit`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then(
          () => {
            this.$bvModal.msgBoxOk('This resource has been updated', {
              centered: true,
              title: 'Resource Updated',
            })
          },
          (err) => {
            this.errors = err.response.data.errors
          }
        )
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>

<style scoped>
.list-card {
  max-height: 400px;
}
</style>
